import { Fade, Modal } from '@material-ui/core'
import React, { useState } from 'react'
import axios from '../../axios'
import { ENDPOINTS, baseURL } from '../../constants'
import { Segment, SEGMENTS } from '../shareLinkWizard/content'
import { styles } from '../shareLinkWizard/styles'

// This 4th option is signup-popup-only — it must never be added to
// `shareLinkWizard/content.ts`'s SEGMENTS, since that list also drives the
// create-share-link declaration wizard, which stays unchanged.
export type SignupSegment = Segment | 'cashback_shopper'

const SIGNUP_SEGMENTS: {
  id: SignupSegment
  title: string
  description: string
}[] = [
  ...SEGMENTS,
  {
    id: 'cashback_shopper',
    title: 'Cashback shopper',
    description: 'Individuals wanting to earn cashback',
  },
]

interface SegmentSelectPopupProps {
  open: boolean
  userId: string | number
  /** Called after the segment is saved so the parent can update appState. */
  onSelected: (segment: SignupSegment) => void
}

/**
 * Onboarding popup that asks a new user to self-classify (influencer,
 * digital marketer, business, or cashback shopper) so
 * `users.share_link_segment` is populated ahead of any future share-link
 * creation. Mirrors the "who are you?" step of `shareLinkWizard` (same
 * cards/copy/styling for the first 3 options) but drops the wizard chrome
 * (eyebrow, heading, close button), adds a signup-only "cashback shopper"
 * option, and cannot be dismissed without a selection — there is no
 * `onClose`/escape/backdrop dismissal and no close button, by design.
 */
const SegmentSelectPopup = ({
  open,
  userId,
  onSelected,
}: SegmentSelectPopupProps) => {
  const classes = styles()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSelect = async (segment: SignupSegment) => {
    if (submitting) return
    setSubmitting(true)
    setError('')
    try {
      await axios.patch(
        `${baseURL}/${ENDPOINTS.user.replace(':id', String(userId))}`,
        {
          shareLinkSegment: segment,
        }
      )
      onSelected(segment)
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Something went wrong, please try again.'
      )
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} disableEscapeKeyDown keepMounted>
      <Fade in={open}>
        <div className={classes.paper}>
          <p className={classes.intro}>
            Select the option that best describes you so we can tailor your
            experience.
          </p>
          {SIGNUP_SEGMENTS.map(s => (
            <button
              key={s.id}
              type="button"
              className={classes.roleCard}
              disabled={submitting}
              onClick={() => handleSelect(s.id)}
            >
              <div>
                <p className={classes.roleTitle}>{s.title}</p>
                <p className={classes.roleDesc}>{s.description}</p>
              </div>
            </button>
          ))}
          {error && <p className={classes.error}>{error}</p>}
        </div>
      </Fade>
    </Modal>
  )
}

export default SegmentSelectPopup
